import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  Typography,
  Chip,
  Tooltip,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getChannelIcon } from "../../utils/utils";
import { sanitizeTelephonyListPreview } from "../../utils/partnerApiAuditDisplay";
import { STATUS_OPTIONS, normalizeStatusInput } from "../StatusChipSelector";

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const sanitizeText = (htmlString) => {
  if (!htmlString) return "—";
  const div = document.createElement("div");
  div.innerHTML = htmlString;
  const text = (div.textContent || div.innerText || "").trim();
  return text || "—";
};

const formatListPreview = (item) => {
  const preview = item?.lastMessagePreview;
  if (preview?.text && preview.text !== "—") {
    const prefix =
      preview.kind === "client"
        ? "Cliente: "
        : preview.kind === "agent"
          ? "Equipe: "
          : "";
    return `${prefix}${sanitizeTelephonyListPreview(preview.text)}`;
  }
  const raw = sanitizeTelephonyListPreview(sanitizeText(item?.lastMessage));
  if (raw && raw !== "—") return raw;
  const channel = item?.channel != null ? String(item.channel).trim() : "";
  if (channel) return channel;
  return "Sem mensagens no histórico";
};

const parseLegacySubject = (description) => {
  if (!description || typeof description !== "string") return "Solicitação";
  const lines = description.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) return lines.slice(1).join(" ");
  return lines[0]?.replace(/^Protocolo:\s*\d+\s*/i, "").trim() || "Solicitação";
};

function HistoryStatusChip({ status, size = "small" }) {
  const theme = useTheme();
  const normalized = normalizeStatusInput(status);
  const current =
    STATUS_OPTIONS.find((s) => s.value === normalized) || STATUS_OPTIONS[0];
  const isLightLabel =
    current.label === "Pendente" ||
    current.label === "Em Atendimento" ||
    current.label === "Contato Futuro";
  const bgColor = theme.palette.status[current.colorKey];
  const textColor = isLightLabel
    ? theme.palette.text.primary
    : theme.palette.common.white;

  return (
    <Tooltip title={current.label}>
      <Chip
        label={current.label}
        size={size}
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          fontSize: "0.7rem",
          height: size === "small" ? 22 : 24,
          cursor: "default",
        }}
      />
    </Tooltip>
  );
}

function HistoryAssigneeChip({ assignee, size = "small" }) {
  const theme = useTheme();
  const type = assignee?.type || "queue";
  const label = assignee?.label || "Fila";

  let bg = theme.palette.grey[700];
  let color = theme.palette.common.white;
  if (type === "user") {
    bg = theme.palette.primary.main;
    color = theme.palette.common.white;
  } else if (type === "chatbot") {
    bg = theme.palette.info.main;
    color = theme.palette.common.white;
  } else if (type === "agent") {
    bg = theme.palette.secondary.main;
    color = theme.palette.common.white;
  }

  return (
    <Chip
      label={label}
      size={size}
      sx={{
        backgroundColor: bg,
        color,
        fontSize: "0.7rem",
        height: 22,
        maxWidth: 140,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    />
  );
}

function resolveAssignee(item) {
  if (item?.assignee?.label) return item.assignee;
  const agentId = item?.agentId != null ? String(item.agentId).trim() : "";
  if (
    agentId &&
    agentId !== "0" &&
    agentId !== "00000000-0000-0000-0000-000000000000"
  ) {
    return { type: "agent", label: item.agentName || "Agente de IA" };
  }
  const userId = item?.userId != null ? Number(item.userId) : 0;
  if (userId > 0) {
    return {
      type: Number(item.userUra) === 1 ? "chatbot" : "user",
      label: item.userName || "Atendente",
      isChatBot: Number(item.userUra) === 1,
    };
  }
  return { type: "queue", label: "Fila" };
}

const History = ({
  history = [],
  selectable = false,
  selectedIds = [],
  onToggleSelect,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const items = Array.isArray(history) ? history : [];
  const selectedSet = new Set((selectedIds || []).map((id) => String(id)));

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
        Nenhuma outra solicitação recente deste cliente.
      </Typography>
    );
  }

  const handleOpen = (id) => {
    if (id == null) return;
    navigate(`/chat/${id}`);
  };

  const stopCheckboxEvent = (event) => {
    event.stopPropagation();
  };

  return (
    <List disablePadding sx={{ mx: selectable ? 0 : -1 }}>
      {items.map((item) => {
        const restricted = Boolean(item.restricted);
        const rawSubject = item.subject?.trim() || "";
        const subject =
          rawSubject && rawSubject !== "Solicitação"
            ? rawSubject
            : parseLegacySubject(item.description);
        const channelType = item.channelType ?? item.channel_type ?? null;
        const assignee = restricted ? null : resolveAssignee(item);
        const isSelected = selectedSet.has(String(item.id));

        const body = (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  lineHeight: 1.3,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subject}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap", fontSize: "0.75rem", flexShrink: 0 }}
              >
                {formatTimestamp(item.timestamp || item.date)}
              </Typography>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.8125rem",
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {restricted
                ? "Você não tem permissão para visualizar este tipo de solicitação"
                : formatListPreview(item)}
            </Typography>

            <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap" mt={0.25}>
              {restricted ? (
                <Chip
                  icon={<LockOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
                  label="Sem permissão"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 22,
                  }}
                />
              ) : (
                getChannelIcon(
                  item.channel ?? null,
                  channelType,
                  item.channelTipo ?? item.channel_tipo ?? null
                )
              )}
              <Tooltip title={restricted ? "Protocolo oculto" : `Protocolo: ${item.id}`}>
                <Chip
                  label={restricted ? "#••••" : `#${item.id}`}
                  size="small"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 22,
                  }}
                />
              </Tooltip>
              {restricted ? null : (
                <>
                  <HistoryAssigneeChip assignee={assignee} />
                  <HistoryStatusChip status={item.status} />
                </>
              )}
            </Box>
          </>
        );

        return (
          <ListItem
            key={item.id}
            onClick={restricted ? undefined : () => handleOpen(item.id)}
            sx={{
              cursor: restricted ? "default" : "pointer",
              flexDirection: selectable ? "row" : "column",
              alignItems: selectable ? "flex-start" : "stretch",
              gap: 0.5,
              py: 1.25,
              px: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderLeft: restricted
                ? `4px solid ${theme.palette.warning.main}`
                : isSelected
                  ? `4px solid ${theme.palette.primary.main}`
                  : "4px solid transparent",
              bgcolor: restricted
                ? theme.palette.action.hover
                : isSelected
                  ? theme.palette.action.selected
                  : undefined,
              "&:hover": restricted
                ? undefined
                : { backgroundColor: theme.palette.action.hover },
            }}
          >
            {selectable ? (
              <Checkbox
                size="small"
                checked={isSelected}
                disabled={restricted}
                onClick={stopCheckboxEvent}
                onMouseDown={stopCheckboxEvent}
                onChange={(event) => {
                  stopCheckboxEvent(event);
                  if (restricted) return;
                  onToggleSelect?.(item.id, event.target.checked);
                }}
                inputProps={{
                  "aria-label": restricted
                    ? "Solicitação sem permissão"
                    : `Selecionar protocolo ${item.id}`,
                }}
                sx={{ mt: -0.25, mr: 0.25, p: 0.5 }}
              />
            ) : null}
            {selectable ? (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {body}
              </Box>
            ) : (
              body
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

export default History;
